import React, { useMemo } from 'react';
import { diffWords } from 'diff';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { GitCompare, Loader2, CheckCircle2, X } from 'lucide-react';

/** Lightweight word-level diff dialog for the admin Editorial review
 * tool (EditorReviewWorkspace.jsx) — used both for "diff before commit"
 * (current chapter content vs. about-to-be-committed AI output) and the
 * chapter History panel's "Compare" action (two past versions).
 *
 * Deliberately simpler than the Writer's own `DiffViewerDialog`: no
 * baseline picker or versions-list dependency — just two arbitrary
 * text blobs and an optional confirm action. */
export function AdminTextDiffDialog({
  open, onClose,
  title = 'Compare changes',
  leftLabel = 'Before', rightLabel = 'After',
  leftText = '', rightText = '',
  confirmLabel, onConfirm, confirmBusy = false,
  confirmIcon: ConfirmIcon = CheckCircle2,
  summaryItems = [],
}) {
  const parts = useMemo(
    () => diffWords(leftText || '', rightText || '', { ignoreCase: false }),
    [leftText, rightText],
  );
  const { added, removed } = useMemo(() => {
    let add = 0, rem = 0;
    for (const p of parts) {
      const wc = p.value ? p.value.trim().split(/\s+/).filter(Boolean).length : 0;
      if (p.added) add += wc;
      else if (p.removed) rem += wc;
    }
    return { added: add, removed: rem };
  }, [parts]);

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose?.(); }}>
      <DialogContent className="max-w-4xl max-h-[85vh] flex flex-col gap-0 p-0" data-testid="admin-text-diff-dialog">
        <DialogHeader className="border-b border-border bg-panel-header/40 px-4 py-3">
          <DialogTitle className="flex items-center gap-2 text-[14px] font-semibold">
            <GitCompare className="h-4 w-4 text-primary" /> {title}
          </DialogTitle>
          <DialogDescription className="text-[13.5px] text-muted-foreground">
            {leftLabel} → {rightLabel} · word-level diff ·{' '}
            <span className="text-success">+{added} added</span> ·{' '}
            <span className="text-destructive">−{removed} removed</span>
          </DialogDescription>
        </DialogHeader>

        {summaryItems.length > 0 && (
          <div className="border-b border-border bg-secondary/30 px-4 py-2.5" data-testid="admin-diff-summary">
            <div className="mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
              Session summary
            </div>
            <ul className="space-y-0.5">
              {summaryItems.map((item, i) => (
                <li key={i} className="flex items-start gap-1.5 text-[11.5px] text-foreground">
                  <CheckCircle2 className="mt-0.5 h-3 w-3 shrink-0 text-emerald-500" />
                  {item}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex-1 overflow-auto p-6 text-[13.5px] font-serif leading-relaxed whitespace-pre-wrap break-words"
             data-testid="admin-text-diff-body">
          {parts.length === 0 || (!leftText && !rightText) ? (
            <div className="py-8 text-center text-[13.5px] text-muted-foreground">Nothing to compare.</div>
          ) : parts.map((p, idx) => {
            const k = `d-${idx}-${p.added ? 'a' : p.removed ? 'r' : 'e'}`;
            if (p.added) {
              return (
                <span key={k} className="mx-0.5 rounded-sm bg-success/15 text-success-foreground underline decoration-success/60 decoration-2 underline-offset-2">
                  {p.value}
                </span>
              );
            }
            if (p.removed) {
              return (
                <span key={k} className="mx-0.5 rounded-sm bg-destructive/15 text-destructive line-through decoration-destructive/60">
                  {p.value}
                </span>
              );
            }
            return <span key={k}>{p.value}</span>;
          })}
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-border bg-background px-4 py-2.5">
          <Button size="sm" variant="ghost" onClick={() => onClose?.()} disabled={confirmBusy}
                  data-testid="admin-text-diff-cancel" className="h-7 gap-1.5 text-[13.5px]">
            <X className="h-3 w-3" /> Cancel
          </Button>
          {onConfirm && (
            <Button size="sm" onClick={onConfirm} disabled={confirmBusy}
                    data-testid="admin-text-diff-confirm"
                    className="h-7 gap-1.5 bg-emerald-600 text-[13.5px] text-white hover:bg-emerald-700">
              {confirmBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <ConfirmIcon className="h-3 w-3" />}
              {confirmLabel || 'Confirm'}
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}


